/**
 * 同意書プレビュー (エディターの左カラム)。
 *
 * 編集中の state をそのまま反映してリアルタイムレンダリング。
 * 公開ページ風スタイル (白背景) で見せる。
 */
import type { ConsentImagePosition, ConsentSection } from '@goenhub/shared';
import { useMemo } from 'react';

import { ConsentSectionRenderer } from '../ConsentSectionRenderer';

interface Props {
  title: string;
  sections: ConsentSection[];
  logoStoragePath: string | null;
  logoPosition: ConsentImagePosition;
  stampStoragePath: string | null;
  stampPosition: ConsentImagePosition;
  stampHidden: boolean;
}

function PositionedImage({
  url,
  position,
  size,
  alt,
}: {
  url: string;
  position: ConsentImagePosition;
  size: number;
  alt: string;
}) {
  const classMap: Record<ConsentImagePosition, string> = {
    'top-left': 'self-start mr-auto',
    'top-center': 'self-center mx-auto',
    'top-right': 'self-end ml-auto',
    'bottom-left': 'self-start mr-auto',
    'bottom-center': 'self-center mx-auto',
    'bottom-right': 'self-end ml-auto',
  };
  return (
    <div className={`flex ${classMap[position]}`}>
      <img
        src={url}
        alt={alt}
        style={{ width: size, height: size, objectFit: 'contain' }}
        className="rounded"
      />
    </div>
  );
}

export function ConsentEditorPreview({
  title,
  sections,
  logoStoragePath,
  logoPosition,
  stampStoragePath,
  stampPosition,
  stampHidden,
}: Props) {
  const { topLogos, bottomLogos } = useMemo(() => {
    const top: Array<{
      url: string;
      position: ConsentImagePosition;
      kind: 'logo' | 'stamp';
    }> = [];
    const bottom: Array<{
      url: string;
      position: ConsentImagePosition;
      kind: 'logo' | 'stamp';
    }> = [];
    if (logoStoragePath) {
      const item = {
        url: logoStoragePath,
        position: logoPosition,
        kind: 'logo' as const,
      };
      (logoPosition.startsWith('top-') ? top : bottom).push(item);
    }
    if (stampStoragePath && !stampHidden) {
      const item = {
        url: stampStoragePath,
        position: stampPosition,
        kind: 'stamp' as const,
      };
      (stampPosition.startsWith('top-') ? top : bottom).push(item);
    }
    return { topLogos: top, bottomLogos: bottom };
  }, [
    logoStoragePath,
    logoPosition,
    stampStoragePath,
    stampPosition,
    stampHidden,
  ]);

  return (
    <div className="bg-white text-gray-900 rounded-lg shadow-sm border border-gray-300 p-6 sm:p-8 max-w-2xl mx-auto">
      {/* ロゴ・印鑑 (上段) */}
      {topLogos.length > 0 && (
        <div className="flex justify-between items-start mb-4 gap-3">
          {topLogos.map((it, i) => (
            <PositionedImage
              key={i}
              url={it.url}
              position={it.position}
              size={it.kind === 'stamp' ? 56 : 72}
              alt={it.kind === 'stamp' ? '印鑑' : 'ロゴ'}
            />
          ))}
        </div>
      )}

      <h1 className="text-xl font-bold text-center mb-6">
        {title || '(タイトル未入力)'}
      </h1>

      <div className="prose prose-sm max-w-none">
        {sections.map((sec) => (
          <ConsentSectionRenderer key={sec.id} section={sec} publicTheme />
        ))}
        {sections.some((s) => s.type === 'signature') && (
          <div className="mt-8 border-t border-gray-300 pt-4">
            <h3 className="text-sm font-semibold mb-1">署名</h3>
            <p className="text-xs text-gray-600">
              （公開ページでは氏名・署名日の入力フォームが表示されます）
            </p>
          </div>
        )}
      </div>

      {bottomLogos.length > 0 && (
        <div className="flex justify-between items-end mt-6 gap-3">
          {bottomLogos.map((it, i) => (
            <PositionedImage
              key={i}
              url={it.url}
              position={it.position}
              size={it.kind === 'stamp' ? 56 : 72}
              alt={it.kind === 'stamp' ? '印鑑' : 'ロゴ'}
            />
          ))}
        </div>
      )}
    </div>
  );
}
